// DurationField — number input + unit select for choosing an access duration.
// design ref: styles.css:874–876 (.duration-row) and styles.css:2854–2869
// (.ra-duration-row, .ra-num-input, .ra-unit-select). The `xs` size mirrors
// styles.css:3217 (.ra-num-input--xs) — used inside per-item rows that need
// to squeeze onto one line.
//
// We re-use the existing ds Select for the unit dropdown (matching `.select`
// + chevron). The number input is bespoke: the design's `.ra-num-input` has
// a different chrome from `.input` (no shadow, white bg, neutral-200 border),
// so we render it inline rather than calling ds/Input.

import { Box, HStack, chakra, forwardRef } from "@chakra-ui/react";
import Select from "./Select";

export type DurationUnit = "minutes" | "hours" | "days" | "weeks" | "months";

export interface DurationValue {
  num: number;
  unit: DurationUnit;
  /**
   * No expiry. Only set where the resource allows it (`allowIndefinite`); when
   * true, `num`/`unit` are placeholders and ignored by submit + display.
   */
  indefinite?: boolean;
}

export interface DurationFieldProps {
  value: DurationValue;
  onChange: (next: DurationValue) => void;
  /** `sm` = 52px wide / 13px font (xs preset); `md` (default) = 72px / 14px. */
  size?: "sm" | "md";
  /** Offer an "Indefinite" option in the unit dropdown (resource-gated). */
  allowIndefinite?: boolean;
}

// Sentinel select value for the indefinite option (not a real DurationUnit).
const INDEFINITE = "indefinite";

const UNITS: DurationUnit[] = ["hours", "days", "weeks", "months"];

const UNIT_LABEL: Record<DurationUnit, string> = {
  minutes: "Minutes",
  hours: "Hours",
  days: "Days",
  weeks: "Weeks",
  months: "Months",
};

// Bespoke number input matching `.ra-num-input` (styles.css:2857). Same
// chrome as our ds/Input but with a fixed width and slightly different
// border tone (neutral-200 vs neutral-300) per the design.
const NumInput = chakra("input", {
  baseStyle: {
    display: "block",
    fontFamily: "inherit",
    color: "neutral.950",
    lineHeight: "1.4",
    bg: "neutral.0",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "neutral.200",
    borderRadius: "component.lg", // 8px
    outline: "none",
    transition: "border-color 120ms, box-shadow 120ms, background-color 120ms",
    _hover: {
      borderColor: "neutral.300",
    },
    _focus: {
      borderColor: "p.400",
      boxShadow: "focusRing",
      outline: "none",
    },
    _focusVisible: {
      borderColor: "p.400",
      boxShadow: "focusRing",
      outline: "none",
    },
    _disabled: {
      bg: "neutral.100",
      color: "neutral.500",
      borderColor: "neutral.200",
      cursor: "not-allowed",
    },
  },
});

const SIZE = {
  md: { width: "72px", px: "md", py: "sm", fontSize: "md" }, // 72 / 8×12 / 14
  sm: { width: "52px", px: "sm", py: "1xs", fontSize: "smd" }, // 52 / 6×8 / 13
} as const;

const DurationField = forwardRef<DurationFieldProps, "div">(
  ({ value, onChange, size = "md", allowIndefinite = false, ...rest }, ref) => {
    const s = SIZE[size];
    const indefinite = Boolean(value.indefinite);
    // Keep the option present if the value is already indefinite, even on a
    // resource that no longer allows it, so the select doesn't render blank.
    const offerIndefinite = allowIndefinite || indefinite;
    // Minutes only come from a cap set in minutes; they are never offered.
    const units: DurationUnit[] =
      value.unit === "minutes" ? ["minutes", ...UNITS] : UNITS;

    const handleUnit = (next: string) => {
      if (next === INDEFINITE) {
        onChange({ num: value.num, unit: value.unit, indefinite: true });
      } else {
        onChange({ num: value.num, unit: next as DurationUnit, indefinite: false });
      }
    };

    return (
      <HStack
        ref={ref}
        align="center"
        spacing="sm" // 8px
        {...rest}
      >
        {/* The amount is meaningless for an indefinite grant — hide it. */}
        {!indefinite && (
          <NumInput
            type="number"
            min={1}
            width={s.width}
            flex="0 0 auto"
            px={s.px}
            py={s.py}
            fontSize={s.fontSize}
            value={value.num}
            onChange={(e) => {
              const n = Math.max(1, parseInt(e.target.value, 10) || 1);
              onChange({ num: n, unit: value.unit, indefinite: false });
            }}
            aria-label="Duration amount"
          />
        )}
        <Box flex="1" minW={0}>
          <Select
            size={size}
            value={indefinite ? INDEFINITE : value.unit}
            onChange={(e) => handleUnit(e.target.value)}
            aria-label="Duration unit"
          >
            {units.map((u) => (
              <option key={u} value={u}>
                {UNIT_LABEL[u]}
              </option>
            ))}
            {offerIndefinite && <option value={INDEFINITE}>Indefinite</option>}
          </Select>
        </Box>
      </HStack>
    );
  },
);

DurationField.displayName = "DurationField";

export default DurationField;
